import { useEffect, useRef } from 'react';
import type { FC, JSX } from 'react';

import * as styles from './CarouselItem.module.scss';

type BaseCarouselItemProps = {
  focusable?: boolean;
} & JSX.IntrinsicElements['li'];

export interface ObservableCarouselItemProps extends BaseCarouselItemProps {
  index: number;
  onIntersectionChange: (
    entry: IntersectionObserverEntry,
    index: number
  ) => void;
}

interface SimpleCarouselItemProps extends BaseCarouselItemProps {
  index?: never;
  onIntersectionChange?: never;
}

export type CarouselItemProps =
  | ObservableCarouselItemProps
  | SimpleCarouselItemProps;

export const CarouselItem: FC<CarouselItemProps> = ({
  index,
  focusable,
  onIntersectionChange,
  children,
  ...props
}) => {
  const ref = useRef<HTMLLIElement>(null!);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        onIntersectionChange?.(entry, index);

        if (focusable && entry.isIntersecting) {
          ref.current.focus();
        }
      },
      { threshold: 1 }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [onIntersectionChange, index, focusable]);

  return (
    <li className={styles.item} ref={ref} {...props}>
      {children}
    </li>
  );
};
